import { cookiesImage } from '@/misc/images';
import { getLocalCookiesConsent, updateConsent } from '@/services/ga-tracking';
import { Dialog, Switch, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type CookiesConsent = ReturnType<typeof getLocalCookiesConsent>

const Cookies = {
  NECESSARY: 'necessary',
  ANALYSIS: 'analytics',
  MARKETING: 'marketing',
} as const;

const CookiesBanner = (_props: {}) => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(true);
  const [isCustomising, setIsCustomising] = useState(false);
  const [enabled, setEnabled] = useState(getLocalCookiesConsent());

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleClickCustomise = () => {
    setIsCustomising(true);
  };

  const handleClickToggle = useCallback(
    (option: keyof CookiesConsent) => {
      setEnabled({ ...enabled, [option]: !enabled[option] });
    },
    [enabled],
  );

  const checkOptions = useMemo(
    () => [
      {
        id: 1,
        onChange: null,
        checked: enabled[Cookies.NECESSARY],
        mandatory: true,
        title: t('translation:cookies.modal.customize.necessary_cookies.title'),
        body: t('translation:cookies.modal.customize.necessary_cookies.body'),
      },
      {
        id: 2,
        onChange: () => handleClickToggle(Cookies.ANALYSIS),
        checked: enabled[Cookies.ANALYSIS],
        mandatory: false,
        title: t('translation:cookies.modal.customize.analysis_cookies.title'),
        body: t('translation:cookies.modal.customize.analysis_cookies.body'),
      },
      {
        id: 3,
        onChange: () => handleClickToggle(Cookies.MARKETING),
        checked: enabled[Cookies.MARKETING],
        mandatory: false,
        title: t('translation:cookies.modal.customize.marketing_cookies.title'),
        body: t('translation:cookies.modal.customize.marketing_cookies.body'),
      },
    ],
    [handleClickToggle, enabled, t],
  );

  const handleClickAccept = () => {
    updateConsent(true, true);
    handleCloseModal();
  };

  const handleClickReject = () => {
    updateConsent(false, false);
    handleCloseModal();
  };

  const handleClickSaveCustom = () => {
    updateConsent(enabled[Cookies.ANALYSIS], enabled[Cookies.MARKETING]);
    handleCloseModal();
  };

  return (
    <Transition.Root show={showModal} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={() => null}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-start p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              {isCustomising ? (
                <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:p-6 md:w-2/3 xl:w-1/2">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-left font-avenir text-fg-primary sm:ml-4 sm:mt-0">
                      <div className="flex gap-3">
                        <img
                          className="h-7"
                          src={cookiesImage}
                          alt={t('translation:accessibility.icon.alt.cookies')}
                        />
                        <Dialog.Title as="h3" className="text-2xl font-semibold">
                          {t('translation:cookies.modal.customize.title')}
                        </Dialog.Title>
                      </div>
                      <div className="mt-4 flex flex-col gap-5 text-sm">
                        <p>
                          {t('translation:cookies.modal.customize.body')}{' '}
                          <Link
                            target="_blank"
                            to="/legal/cookies"
                            className="text-linkBlue underline outline-none"
                          >
                            {t('translation:cookies.modal.cookies_policy')}
                          </Link>
                          .
                        </p>
                        <div>
                          {checkOptions.map((option) => (
                            <div key={option.id}>
                              <h3 className="text-base font-bold">{option.title}</h3>
                              <div className="flex justify-between gap-2">
                                <p>{option.body}</p>
                                <Switch
                                  onChange={option.onChange ?? undefined}
                                  checked={option.checked}
                                  className={clsx(
                                    {
                                      'bg-bg-orange-hover': option.checked,
                                      'bg-gray-200': !option.checked,
                                      'cursor-not-allowed opacity-40': option.mandatory,
                                    },
                                    'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-0',
                                  )}
                                >
                                  <span
                                    aria-hidden="true"
                                    className={clsx(
                                      {
                                        'translate-x-5': option.checked,
                                        'translate-x-0': !option.checked,
                                      },
                                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                    )}
                                  />
                                </Switch>
                              </div>
                              <hr className="my-4" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-col gap-5 md:mt-4 md:flex-row md:justify-end">
                    <button
                      type="button"
                      className="inline-flex w-full  justify-center rounded-md bg-[#F0F0F0] px-5 py-2 font-phosphate text-sm text-[#898989] shadow-sm hover:bg-gray-200 md:w-1/3"
                      onClick={handleClickReject}
                    >
                      {t('translation:cookies.modal.customize.button_cancel')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-bg-orange px-5 py-2 font-phosphate text-sm text-white shadow-sm hover:bg-bg-orange-hover md:w-1/3"
                      onClick={handleClickSaveCustom}
                    >
                      {t('translation:cookies.modal.customize.button_save')}
                    </button>
                  </div>
                </Dialog.Panel>
              ) : (
                <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:p-6 md:w-2/3 xl:w-1/2">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-left font-avenir text-[#29323C] sm:ml-4 sm:mt-0">
                      <div className="flex gap-3">
                        <img
                          className="h-7"
                          src={cookiesImage}
                          alt={t('translation:accessibility.icon.alt.cookies')}
                        />
                        <Dialog.Title as="h3" className="text-2xl font-semibold">
                          {t('translation:cookies.modal.title')}
                        </Dialog.Title>
                      </div>
                      <div className="mt-4 text-sm">
                        {t('translation:cookies.modal.body1')}{' '}
                        <Link
                          target="_blank"
                          to="/legal/cookies"
                          className="text-linkBlue underline outline-none"
                        >
                          {t('translation:cookies.modal.cookies_policy')}
                        </Link>
                        . {t('translation:cookies.modal.body2')}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-col gap-5 md:mt-4 md:flex-row md:justify-end">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-[#F0F0F0] px-5 py-2 font-phosphate text-sm text-[#898989] shadow-sm hover:bg-gray-200 md:w-28"
                      onClick={handleClickCustomise}
                    >
                      {t('translation:cookies.modal.customize_button')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-[#F0F0F0] px-5 py-2 font-phosphate text-sm text-[#898989] shadow-sm hover:bg-gray-200 md:w-28"
                      onClick={handleClickReject}
                    >
                      {t('translation:cookies.modal.reject_button')}
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-bg-orange-hover px-5 py-2 font-phosphate text-sm text-white shadow-sm hover:bg-bg-orange md:w-28"
                      onClick={handleClickAccept}
                    >
                      {t('translation:cookies.modal.accept_button')}
                    </button>
                  </div>
                </Dialog.Panel>
              )}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CookiesBanner;
